/**
 * Settings → Organization → Workspace & contracting entity.
 *
 * The only UI that writes `PUT /api/v1/settings/general`: the workspace name and
 * timezone, plus the contracting Customer (T&C 4.2) — legal name, country and
 * company size — captured at sign-up and correctable here until the first
 * invoice goes out. The write is gated on `tenant.manage` server-side; the tab
 * itself is not, so a member without it gets a readable 403 toast.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { COUNTRIES } from '../../config/countries';
import { useGeneralSettings, useUpdateGeneralSettings } from '../../services/settings';
import {
  COMPANY_SIZES,
  CompanySize,
  GeneralSettings,
  UpdateGeneralSettingsData,
} from '../../types/settings';

/** Field chrome for this surface — sits on the card's `bg-surface-primary`. */
const FIELD_CLASS =
  'w-full h-11 px-3.5 text-sm rounded-xl border border-border-primary/20 ' +
  'bg-surface-primary dark:bg-surface-elevated text-text-primary outline-none ' +
  'transition-colors focus:border-primary-500 disabled:opacity-60 disabled:cursor-not-allowed';

const SELECT_CLASS = `${FIELD_CLASS} pr-10 appearance-none cursor-pointer`;

/**
 * Curated zone list. The server stores any IANA name, so the tenant's current
 * value is folded in below — never dropped just because it is not listed here.
 */
const COMMON_TIMEZONES: readonly string[] = [
  'UTC',
  'Europe/London',
  'Europe/Lisbon',
  'Europe/Madrid',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Warsaw',
  'Europe/Athens',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
];

interface OrganizationForm {
  tenant_name: string;
  legal_name: string;
  country: string;
  tax_number: string;
  company_size: string;
  timezone: string;
}

const EMPTY_FORM: OrganizationForm = {
  tenant_name: '',
  legal_name: '',
  country: '',
  tax_number: '',
  company_size: '',
  timezone: 'UTC',
};

const FORM_FIELDS = Object.keys(EMPTY_FORM) as Array<keyof OrganizationForm>;

const toForm = (settings: GeneralSettings): OrganizationForm => ({
  tenant_name: settings.tenant_name ?? '',
  legal_name: settings.legal_name ?? '',
  tax_number: (settings.tax_number ?? '').toUpperCase(),
  // Stored uppercase server-side; normalize so the <select> matches an option.
  country: (settings.country ?? '').toUpperCase(),
  company_size: settings.company_size ?? '',
  timezone: settings.timezone || 'UTC',
});

const errorStatus = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } })?.response?.status;

const errorMessage = (err: unknown, fallback: string): string => {
  if (errorStatus(err) === 403) {
    return 'You do not have permission to change workspace details. Ask an owner or admin.';
  }
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback
  );
};

const fieldErrorsFrom = (err: unknown): Record<string, string> => {
  const raw = (err as { response?: { data?: { errors?: Record<string, string[] | string> } } })
    ?.response?.data?.errors;
  if (!raw) return {};
  return Object.entries(raw).reduce<Record<string, string>>((acc, [field, messages]) => {
    acc[field] = Array.isArray(messages) ? messages.join(' ') : String(messages);
    return acc;
  }, {});
};

/** Error line wired to its field through aria-describedby. */
const FieldError: React.FC<{ id: string; message: string }> = ({ id, message }) => (
  <p id={id} role="alert" className="mt-1 text-xs text-error-600 dark:text-error-400">
    {message}
  </p>
);

/** Native select + decorative chevron (no portal, no clipping, keyboard-solid). */
const SelectShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="relative">
    {children}
    <ChevronDownIcon
      aria-hidden="true"
      className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary"
    />
  </div>
);

export default function OrganizationSettings() {
  const { data, isLoading, isError } = useGeneralSettings();
  const updateSettings = useUpdateGeneralSettings();

  const [form, setForm] = useState<OrganizationForm>(EMPTY_FORM);
  // Server snapshot: what we diff against so only touched fields are sent.
  const [baseline, setBaseline] = useState<OrganizationForm>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!data) return;
    const next = toForm(data);
    setForm(next);
    setBaseline(next);
  }, [data]);

  const setField = (field: keyof OrganizationForm) => (value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
  };

  const timezoneOptions = useMemo(
    () =>
      form.timezone && !COMMON_TIMEZONES.includes(form.timezone)
        ? [form.timezone, ...COMMON_TIMEZONES]
        : COMMON_TIMEZONES,
    [form.timezone]
  );

  const saving = updateSettings.isPending;
  const disabled = isLoading || saving;
  const isDirty = FORM_FIELDS.some((key) => form[key] !== baseline[key]);

  /** Only the fields the user actually touched; '' on an optional one clears it. */
  const buildPayload = (): UpdateGeneralSettingsData => {
    const payload: UpdateGeneralSettingsData = {};
    if (form.tenant_name !== baseline.tenant_name) payload.tenant_name = form.tenant_name.trim();
    if (form.timezone !== baseline.timezone) payload.timezone = form.timezone;
    if (form.legal_name !== baseline.legal_name) {
      payload.legal_name = form.legal_name.trim() || null;
    }
    if (form.country !== baseline.country) payload.country = form.country || null;
    if (form.tax_number !== baseline.tax_number) {
      payload.tax_number = form.tax_number.trim().toUpperCase() || null;
    }
    if (form.company_size !== baseline.company_size) {
      payload.company_size = form.company_size ? (form.company_size as CompanySize) : null;
    }
    return payload;
  };

  const handleSave = async () => {
    const tenantName = form.tenant_name.trim();
    const legalName = form.legal_name.trim();
    if (!tenantName) {
      setFieldErrors({ tenant_name: 'Give the workspace a name.' });
      return;
    }
    if (legalName.length === 1) {
      setFieldErrors({ legal_name: 'Enter at least 2 characters, or leave it empty.' });
      return;
    }

    const payload = buildPayload();
    if (Object.keys(payload).length === 0) return;

    setFieldErrors({});
    try {
      await updateSettings.mutateAsync(payload);
      const saved = { ...form, tenant_name: tenantName, legal_name: legalName };
      setForm(saved);
      setBaseline(saved);
      toast.success('Workspace updated');
    } catch (err) {
      setFieldErrors(fieldErrorsFrom(err));
      toast.error(errorMessage(err, 'Could not save workspace details. Please try again.'));
    }
  };

  const handleDiscard = () => {
    setForm(baseline);
    setFieldErrors({});
  };

  const legalNameDescribedBy = fieldErrors.legal_name ? 'legal-name-error' : 'legal-name-hint';

  return (
    <section>
      <h2 className="text-lg font-semibold text-text-primary mb-1">Workspace</h2>
      <p className="text-sm text-text-secondary mb-6">
        The workspace name your members see, and the entity we contract and invoice
      </p>

      {isError && (
        <p role="alert" className="mb-4 text-sm text-error-600 dark:text-error-400">
          Couldn&apos;t load your workspace details. Reload the page to try again.
        </p>
      )}

      <div
        className="bg-surface-primary border border-border-primary/10 rounded-xl p-6 space-y-6 max-w-2xl"
        aria-busy={disabled || undefined}
      >
        <div>
          <label htmlFor="tenant-name" className="block text-sm font-medium text-text-primary mb-2">
            Workspace name
          </label>
          <input
            id="tenant-name"
            type="text"
            value={form.tenant_name}
            onChange={(e) => setField('tenant_name')(e.target.value)}
            disabled={disabled}
            maxLength={255}
            aria-invalid={fieldErrors.tenant_name ? true : undefined}
            aria-describedby={fieldErrors.tenant_name ? 'tenant-name-error' : undefined}
            className={FIELD_CLASS}
          />
          {fieldErrors.tenant_name && (
            <FieldError id="tenant-name-error" message={fieldErrors.tenant_name} />
          )}
        </div>

        <div>
          <label htmlFor="timezone" className="block text-sm font-medium text-text-primary mb-2">
            Timezone
          </label>
          <SelectShell>
            <select
              id="timezone"
              value={form.timezone}
              onChange={(e) => setField('timezone')(e.target.value)}
              disabled={disabled}
              aria-invalid={fieldErrors.timezone ? true : undefined}
              aria-describedby={fieldErrors.timezone ? 'timezone-error' : undefined}
              className={SELECT_CLASS}
            >
              {timezoneOptions.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </SelectShell>
          {fieldErrors.timezone && (
            <FieldError id="timezone-error" message={fieldErrors.timezone} />
          )}
        </div>

        {/* Contracting customer — the entity that signed the Terms & Conditions
            and that billing puts on the invoice (T&C 4.2). */}
        <div className="pt-6 border-t border-border-primary/40 space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-text-primary">Contracting entity</h3>
            <p className="mt-1 text-sm text-text-secondary">
              Used on your invoices and to determine the tax treatment of your subscription.
            </p>
          </div>

          <div>
            <label
              htmlFor="legal-name"
              className="block text-sm font-medium text-text-primary mb-2"
            >
              Legal name
            </label>
            <input
              id="legal-name"
              type="text"
              value={form.legal_name}
              onChange={(e) => setField('legal_name')(e.target.value)}
              disabled={disabled}
              maxLength={255}
              autoComplete="organization"
              aria-invalid={fieldErrors.legal_name ? true : undefined}
              aria-describedby={legalNameDescribedBy}
              className={FIELD_CLASS}
            />
            {fieldErrors.legal_name ? (
              <FieldError id="legal-name-error" message={fieldErrors.legal_name} />
            ) : (
              <p id="legal-name-hint" className="mt-1 text-xs text-text-secondary">
                {form.legal_name.trim()
                  ? 'The registered company or professional name that appears on your invoices.'
                  : `Optional. While this is empty, invoices use your workspace name${
                      form.tenant_name.trim() ? ` (${form.tenant_name.trim()})` : ''
                    }.`}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="tax-number"
              className="block text-sm font-medium text-text-primary mb-2"
            >
              VAT / tax number
            </label>
            <input
              id="tax-number"
              type="text"
              value={form.tax_number}
              onChange={(e) => setField('tax_number')(e.target.value.toUpperCase())}
              disabled={disabled}
              maxLength={32}
              placeholder="e.g. FR12345678901"
              aria-describedby="tax-number-hint"
              className={FIELD_CLASS}
            />
            <p id="tax-number-hint" className="mt-1 text-xs text-text-secondary">
              {/* The number is what makes an EU sale business-to-business; a
                  customer who cannot give one is invoiced with VAT
                  (Aleix, 2026-09-28). */}
              Businesses in the EU outside Spain: give your intra-community VAT
              number and your invoice is issued without VAT, under reverse
              charge. Leave it empty and VAT is charged as normal.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label htmlFor="country" className="block text-sm font-medium text-text-primary mb-2">
                Country
              </label>
              <SelectShell>
                <select
                  id="country"
                  value={form.country}
                  onChange={(e) => setField('country')(e.target.value)}
                  disabled={disabled}
                  aria-invalid={fieldErrors.country ? true : undefined}
                  aria-describedby={fieldErrors.country ? 'country-error' : undefined}
                  className={SELECT_CLASS}
                >
                  <option value="">Not set</option>
                  {COUNTRIES.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </SelectShell>
              {fieldErrors.country && (
                <FieldError id="country-error" message={fieldErrors.country} />
              )}
            </div>

            <div>
              <label
                htmlFor="company-size"
                className="block text-sm font-medium text-text-primary mb-2"
              >
                Company size
              </label>
              <SelectShell>
                <select
                  id="company-size"
                  value={form.company_size}
                  onChange={(e) => setField('company_size')(e.target.value)}
                  disabled={disabled}
                  aria-invalid={fieldErrors.company_size ? true : undefined}
                  aria-describedby={fieldErrors.company_size ? 'company-size-error' : undefined}
                  className={SELECT_CLASS}
                >
                  <option value="">Not set</option>
                  {COMPANY_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size} employees
                    </option>
                  ))}
                </select>
              </SelectShell>
              {fieldErrors.company_size && (
                <FieldError id="company-size-error" message={fieldErrors.company_size} />
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2 border-t border-border-primary/40">
          <button
            type="button"
            onClick={handleSave}
            disabled={disabled || !isDirty}
            className="btn btn-primary rounded-full active:scale-[0.97] disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save workspace'}
          </button>
          <button
            type="button"
            onClick={handleDiscard}
            disabled={disabled || !isDirty}
            className="btn btn-ghost rounded-full disabled:opacity-50"
          >
            Discard changes
          </button>
        </div>
      </div>
    </section>
  );
}
